import { useState, useEffect } from 'react';

// Global flag to prevent multiple loading attempts
let isLoadingGlobally = false;
let globalLoadPromise = null;

const useGoogleMapsLoader = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadGoogleMapsAPI = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        // Check if Google Maps API is already loaded
        if (window.google && window.google.maps) {
          setIsLoaded(true);
          setIsLoading(false);
          return;
        }

        // If already loading globally, wait for the existing promise
        if (isLoadingGlobally && globalLoadPromise) {
          try {
            await globalLoadPromise;
            if (window.google && window.google.maps) {
              setIsLoaded(true);
              setIsLoading(false);
              return;
            }
          } catch (error) {
            setLoadError(error);
            setIsLoading(false);
            return;
          }
        }

        // Set global loading flag
        isLoadingGlobally = true;
        
        // Create a promise for this loading attempt
        globalLoadPromise = new Promise(async (resolve, reject) => {
          try {
            // Check if script already exists
            const existingScript = document.querySelector('#google-maps-api-script');
            if (existingScript) {
              console.log('Google Maps script already exists, reusing it');
              // If script exists, wait for it to load
              if (existingScript.complete) {
                setTimeout(() => {
                  if (window.google && window.google.maps) {
                    console.log('Google Maps API already loaded');
                    setIsLoaded(true);
                    setIsLoading(false);
                    isLoadingGlobally = false;
                    resolve();
                  } else {
                    const error = new Error('Google Maps API failed to initialize from existing script');
                    setLoadError(error);
                    setIsLoading(false);
                    isLoadingGlobally = false;
                    reject(error);
                  }
                }, 100);
                return;
              }
            }
            
                                     // Load Google Maps API from backend
            console.log('Loading Google Maps API from backend...');
            
            // Fetch the script content from backend
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/maps/load`);
             if (!response.ok) {
               throw new Error(`Failed to fetch Google Maps API: ${response.status} ${response.statusText}`);
             }
             
                         const scriptContent = await response.text();
             
             // Check if the response is HTML (error page) instead of JavaScript
             if (scriptContent.trim().startsWith('<!DOCTYPE html>') || scriptContent.trim().startsWith('<html>') || scriptContent.includes('<body>')) {
               console.error('Backend returned HTML instead of JavaScript. This could mean:');
               console.error('1. The backend is not running or REACT_APP_API_BASE_URL is not configured correctly');
               console.error('2. The proxy is not working correctly');
               console.error('3. The Google API key is not configured');
               console.error('4. There is a routing issue');
               
               // Try to load Google Maps directly from Google's CDN as a fallback
               console.log('Attempting to load Google Maps directly from Google CDN...');
               const script = document.createElement('script');
               script.id = 'google-maps-api-script';
               script.src = 'https://maps.googleapis.com/maps/api/js?key=YOUR_API_KEY&libraries=places';
               script.async = true;
               script.defer = true;
               
               script.onload = () => {
                 console.log('Google Maps loaded from CDN successfully');
                 setTimeout(() => {
                   if (window.google && window.google.maps) {
                     console.log('Google Maps API initialized successfully from CDN');
                     setIsLoaded(true);
                     setIsLoading(false);
                     isLoadingGlobally = false;
                     resolve();
                   } else {
                     const error = new Error('Google Maps API failed to initialize from CDN');
                     setLoadError(error);
                     setIsLoading(false);
                     isLoadingGlobally = false;
                     reject(error);
                   }
                 }, 100);
               };
               
               script.onerror = (error) => {
                 console.error('Failed to load Google Maps from CDN:', error);
                 const loadError = new Error('Failed to load Google Maps API. Please ensure the backend is running and the Google API key is configured.');
                 setLoadError(loadError);
                 setIsLoading(false);
                 isLoadingGlobally = false;
                 reject(loadError);
               };
               
               document.head.appendChild(script);
               return;
             }
            
            // Create and execute the script
            const script = document.createElement('script');
            script.id = 'google-maps-api-script';
            script.type = 'text/javascript';
            script.textContent = scriptContent;
            
            script.onload = () => {
              // Wait a bit for the API to fully initialize
              setTimeout(() => {
                if (window.google && window.google.maps) {
                  setIsLoaded(true);
                  setIsLoading(false);
                  isLoadingGlobally = false;
                  resolve();
                } else {
                  const error = new Error('Google Maps API failed to initialize');
                  setLoadError(error);
                  setIsLoading(false);
                  isLoadingGlobally = false;
                  reject(error);
                }
              }, 100);
            };

            script.onerror = (error) => {
              console.error('Failed to load Google Maps API script:', error);
              const loadError = new Error('Failed to load Google Maps API. Please check if the backend is running and the Google API key is configured.');
              setLoadError(loadError);
              setIsLoading(false);
              isLoadingGlobally = false;
              reject(loadError);
            };

            document.head.appendChild(script);
          } catch (error) {
            console.error('Error in Google Maps loading:', error);
            setLoadError(error);
            setIsLoading(false);
            isLoadingGlobally = false;
            reject(error);
          }
        });

        // Wait for the promise to resolve
        await globalLoadPromise;
      } catch (error) {
        setLoadError(error);
        setIsLoading(false);
        isLoadingGlobally = false;
      }
    };

    loadGoogleMapsAPI();
  }, []);


  
  return { isLoaded, loadError, isLoading };
};

// Fallback hook for when Google Maps is not available
export const useGoogleMapsFallback = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(new Error('Google Maps API is not available. Please ensure the backend is running and the Google API key is configured.'));
  const [isLoading, setIsLoading] = useState(false);

  return { isLoaded, loadError, isLoading };
};

// Simple hook that always returns loaded state (for development/testing)
export const useGoogleMapsMock = () => {
  const [isLoaded, setIsLoaded] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  return { isLoaded, loadError, isLoading };
};

export default useGoogleMapsLoader;
